import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  DeleteSharp,
  KeyboardArrowDownRounded,
  PaymentsSharp,
  ReceiptSharp,
  ReportProblemSharp,
  ShoppingBagSharp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  getFAQSettings,
  updateFAQSettings,
} from "../../services/crm.service";
import type { FAQAnswer, FAQCategoryData } from "../../types/crm";

type FaqCategoryId =
  | "product-inquiry"
  | "payment-issue"
  | "order-change-request"
  | "urgent-issue";

type FaqCategoryMeta = {
  id: FaqCategoryId;
  title: string;
  description: string;
  icon: ReactNode;
};

type ValidationErrors = Record<string, { subject?: boolean; details?: boolean }>;

const DETAILS_MAX = 200;

const categoryMeta: FaqCategoryMeta[] = [
  {
    id: "product-inquiry",
    title: "Product Inquiry",
    description:
      "Information about product details, features, and availability.",
    icon: <ShoppingBagSharp sx={{ fontSize: 20 }} />,
  },
  {
    id: "payment-issue",
    title: "Payment Issue",
    description:
      "Information about payment methods, transactions, and billing issues.",
    icon: <PaymentsSharp sx={{ fontSize: 20 }} />,
  },
  {
    id: "order-change-request",
    title: "Order Change Request",
    description: "Information about changing or updating an existing order.",
    icon: <ReceiptSharp sx={{ fontSize: 20 }} />,
  },
  {
    id: "urgent-issue",
    title: "Urgent Issue",
    description: "Information for handling urgent or time-sensitive issues.",
    icon: <ReportProblemSharp sx={{ fontSize: 20 }} />,
  },
];

const buildEmptyCategories = (): FAQCategoryData[] =>
  categoryMeta.map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    answers: [],
  }));

export default function CrmHelp() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [expandedCategory, setExpandedCategory] =
    useState<FaqCategoryId>("product-inquiry");
  const [categories, setCategories] = useState<FAQCategoryData[]>(
    buildEmptyCategories(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{
    categoryId: string;
    answerId: string;
    answerLabel: string;
  } | null>(null);

  // Discard confirmation
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Navigation blocker confirmation
  const [showBlockerDialog, setShowBlockerDialog] = useState(false);
  const blockerRef = useRef<ReturnType<typeof useBlocker> | null>(null);

  const blocker = useBlocker(isDirty);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowBlockerDialog(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    setBreadcrumbs([{ label: "FAQ Management", to: "/crm/help" }]);
  }, [setBreadcrumbs]);

  // Track saved snapshot for dirty detection
  const savedSnapshotRef = useRef<string>("");

  const loadFAQ = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const response = await getFAQSettings();
      const loaded =
        response?.categories && response.categories.length > 0
          ? response.categories
          : buildEmptyCategories();
      setCategories(loaded);
      savedSnapshotRef.current = JSON.stringify(loaded);
      setIsDirty(false);
      setValidationErrors({});
    } catch (error: unknown) {
      const empty = buildEmptyCategories();
      setCategories(empty);
      savedSnapshotRef.current = JSON.stringify(empty);

      // 404 means no FAQ data saved yet — show empty form, not an error
      const status =
        (error as { status?: number })?.status ??
        (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFAQ();
  }, [loadFAQ]);

  // Update dirty state whenever categories change
  useEffect(() => {
    if (!savedSnapshotRef.current) return;
    setIsDirty(JSON.stringify(categories) !== savedSnapshotRef.current);
  }, [categories]);

  const getAnswersForCategory = (categoryId: string): FAQAnswer[] => {
    return categories.find((c) => c.id === categoryId)?.answers ?? [];
  };

  const answerCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cat of categories) {
      map[cat.id] = cat.answers.length;
    }
    return map;
  }, [categories]);

  const handleCreateAnswer = () => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === expandedCategory
          ? {
              ...cat,
              answers: [
                ...cat.answers,
                { id: `a-${Date.now()}`, subject: "", details: "" },
              ],
            }
          : cat,
      ),
    );
  };

  const handleAnswerChange = (
    categoryId: string,
    answerId: string,
    field: keyof Pick<FAQAnswer, "subject" | "details">,
    value: string,
  ) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              answers: cat.answers.map((answer) =>
                answer.id === answerId
                  ? {
                      ...answer,
                      [field]:
                        field === "details"
                          ? value.slice(0, DETAILS_MAX)
                          : value,
                    }
                  : answer,
              ),
            }
          : cat,
      ),
    );

    // Clear validation error for the field being edited
    if (validationErrors[answerId]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        if (updated[answerId]) {
          updated[answerId] = { ...updated[answerId], [field]: false };
          if (!updated[answerId].subject && !updated[answerId].details) {
            delete updated[answerId];
          }
        }
        return updated;
      });
    }
  };

  const confirmDeleteAnswer = (
    categoryId: string,
    answerId: string,
    answerIndex: number,
  ) => {
    setDeleteTarget({
      categoryId,
      answerId,
      answerLabel: `Answer ${answerIndex + 1}`,
    });
  };

  const handleDeleteConfirmed = () => {
    if (!deleteTarget) return;
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === deleteTarget.categoryId
          ? {
              ...cat,
              answers: cat.answers.filter(
                (a) => a.id !== deleteTarget.answerId,
              ),
            }
          : cat,
      ),
    );
    // Clear validation errors for deleted answer
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[deleteTarget.answerId];
      return updated;
    });
    setDeleteTarget(null);
  };

  const handleMoveAnswer = (
    categoryId: string,
    answerIndex: number,
    direction: "up" | "down",
  ) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== categoryId) return cat;
        const newAnswers = [...cat.answers];
        const targetIndex =
          direction === "up" ? answerIndex - 1 : answerIndex + 1;
        if (targetIndex < 0 || targetIndex >= newAnswers.length) return cat;
        [newAnswers[answerIndex], newAnswers[targetIndex]] = [
          newAnswers[targetIndex],
          newAnswers[answerIndex],
        ];
        return { ...cat, answers: newAnswers };
      }),
    );
  };

  const handleDiscard = () => {
    if (isDirty) {
      setDiscardDialogOpen(true);
    } else {
      loadFAQ();
      setExpandedCategory("product-inquiry");
    }
  };

  const handleDiscardConfirmed = () => {
    setDiscardDialogOpen(false);
    setValidationErrors({});
    loadFAQ();
    setExpandedCategory("product-inquiry");
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};
    let hasError = false;

    for (const cat of categories) {
      for (const answer of cat.answers) {
        const answerErrors: { subject?: boolean; details?: boolean } = {};
        if (!answer.subject.trim()) {
          answerErrors.subject = true;
          hasError = true;
        }
        if (!answer.details.trim()) {
          answerErrors.details = true;
          hasError = true;
        }
        if (answerErrors.subject || answerErrors.details) {
          errors[answer.id] = answerErrors;
        }
      }
    }

    setValidationErrors(errors);

    // Expand the first category that has errors
    if (hasError) {
      for (const cat of categories) {
        for (const answer of cat.answers) {
          if (errors[answer.id]) {
            setExpandedCategory(cat.id as FaqCategoryId);
            return false;
          }
        }
      }
    }

    return !hasError;
  };

  const handleSave = async () => {
    if (!validate()) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      setIsSaving(true);
      await updateFAQSettings({ categories });
      savedSnapshotRef.current = JSON.stringify(categories);
      setIsDirty(false);
      showSnackbar("FAQ saved successfully", "success");
    } catch {
      showSnackbar("Failed to save FAQ", "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }} spacing={2}>
        <Typography color="error">Failed to load FAQ data</Typography>
        <Button variant="contained" onClick={loadFAQ} sx={{ textTransform: "none" }}>
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={3}>
       

        <Paper
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            boxShadow:
              "0px 1px 3px rgba(0,0,0,0.12), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.2)",
          }}
        >
          <Stack>
            <Box sx={{ px: 3, pt: 2.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#3D3D3D" }}
              >
                FAQ Info
              </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
              {categoryMeta.map((meta, index) => {
                const isExpanded = expandedCategory === meta.id;
                const answers = getAnswersForCategory(meta.id);
                const count = answerCountMap[meta.id] ?? 0;

                return (
                  <Box key={meta.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        py: 2,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                        borderRadius: 1,
                        px: 1,
                        mx: -1,
                      }}
                      onClick={() => setExpandedCategory(meta.id)}
                      role="button"
                      aria-expanded={isExpanded}
                      aria-controls={`faq-category-${meta.id}`}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpandedCategory(meta.id);
                        }
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ color: "#111111" }}
                      >
                        {meta.icon}
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, fontSize: 20 }}
                        >
                          {meta.title}
                        </Typography>
                        <Chip
                          label={`${count} answer${count !== 1 ? "s" : ""}`}
                          size="small"
                          sx={{
                            bgcolor: count > 0 ? "#E8F5E9" : "#F5F5F5",
                            color: count > 0 ? "#2E7D32" : "#9E9E9E",
                            fontWeight: 500,
                            fontSize: 12,
                            height: 24,
                          }}
                        />
                      </Stack>

                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedCategory(meta.id);
                        }}
                        aria-label={
                          isExpanded
                            ? `Collapse ${meta.title}`
                            : `Expand ${meta.title}`
                        }
                        sx={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 180ms ease",
                        }}
                      >
                        <KeyboardArrowDownRounded />
                      </IconButton>
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ color: "#8E95A2", mb: 2 }}
                    >
                      {meta.description}
                    </Typography>

                    {isExpanded && (
                      <Stack
                        spacing={3}
                        id={`faq-category-${meta.id}`}
                        role="region"
                        aria-label={`${meta.title} answers`}
                      >
                        <Button
                          variant="contained"
                          startIcon={<AddRounded />}
                          onClick={handleCreateAnswer}
                          sx={{
                            width: "fit-content",
                            px: 3,
                            height: 40,
                            borderRadius: "96px",
                            textTransform: "none",
                            bgcolor: "#111111",
                            "&:hover": { bgcolor: "#000000" },
                          }}
                        >
                          Create Answer
                        </Button>

                        {answers.length === 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#9E9E9E",
                              textAlign: "center",
                              py: 3,
                              fontStyle: "italic",
                            }}
                          >
                            No answers yet. Click &quot;Create Answer&quot; to
                            add one.
                          </Typography>
                        )}

                        {answers.map((answer, answerIndex) => {
                          const errors = validationErrors[answer.id];
                          return (
                            <Paper
                              key={answer.id}
                              variant="outlined"
                              sx={{ p: 2.5, borderColor: "#E0E0E0" }}
                            >
                              <Stack spacing={2.5}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: 20,
                                      color: "#111111",
                                    }}
                                  >
                                    {`Answer ${answerIndex + 1}`}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <IconButton
                                      size="small"
                                      disabled={answerIndex === 0}
                                      onClick={() =>
                                        handleMoveAnswer(
                                          meta.id,
                                          answerIndex,
                                          "up",
                                        )
                                      }
                                      aria-label="Move up"
                                    >
                                      <ArrowUpwardRounded fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      disabled={
                                        answerIndex === answers.length - 1
                                      }
                                      onClick={() =>
                                        handleMoveAnswer(
                                          meta.id,
                                          answerIndex,
                                          "down",
                                        )
                                      }
                                      aria-label="Move down"
                                    >
                                      <ArrowDownwardRounded fontSize="small" />
                                    </IconButton>
                                    <Button
                                      variant="text"
                                      color="error"
                                      startIcon={<DeleteSharp />}
                                      onClick={() =>
                                        confirmDeleteAnswer(
                                          meta.id,
                                          answer.id,
                                          answerIndex,
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        minWidth: "auto",
                                        px: 1,
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </Stack>

                                <TextField
                                  label="Subject*"
                                  value={answer.subject}
                                  onChange={(event) =>
                                    handleAnswerChange(
                                      meta.id,
                                      answer.id,
                                      "subject",
                                      event.target.value,
                                    )
                                  }
                                  fullWidth
                                  error={!!errors?.subject}
                                  helperText={
                                    errors?.subject
                                      ? "Subject is required"
                                      : undefined
                                  }
                                />

                                <TextField
                                  label="Details*"
                                  value={answer.details}
                                  onChange={(event) =>
                                    handleAnswerChange(
                                      meta.id,
                                      answer.id,
                                      "details",
                                      event.target.value,
                                    )
                                  }
                                  fullWidth
                                  multiline
                                  minRows={4}
                                  error={!!errors?.details}
                                  helperText={
                                    errors?.details
                                      ? "Details is required"
                                      : `${answer.details.length}/${DETAILS_MAX}`
                                  }
                                  slotProps={{
                                    formHelperText: {
                                      sx: {
                                        textAlign: errors?.details
                                          ? "left"
                                          : "right",
                                        mt: 0.5,
                                        mr: 0,
                                      },
                                    },
                                  }}
                                />
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}

                    {index < categoryMeta.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                borderTop: "1px solid #D8DBDF",
                p: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleDiscard}
                disabled={isSaving}
                sx={{
                  borderColor: "#111111",
                  color: "#111111",
                  borderRadius: "96px",
                  px: 3,
                  textTransform: "none",
                }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  borderRadius: "96px",
                  px: 3,
                  textTransform: "none",
                  bgcolor: "#111111",
                  "&:hover": { bgcolor: "#000000" },
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      {/* Delete confirmation dialog */}
      <CustomConfirmDialog
        open={!!deleteTarget}
        title="Delete Answer"
        onClose={() => setDeleteTarget(null)}
        content={
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.answerLabel}</strong>? This action cannot be
            undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1 }}>
            <Button
              onClick={() => setDeleteTarget(null)}
              sx={{ textTransform: "none", color: "#111111" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      {/* Discard confirmation dialog */}
      <CustomConfirmDialog
        open={discardDialogOpen}
        title="Discard Changes"
        onClose={() => setDiscardDialogOpen(false)}
        content={
          <Typography>
            You have unsaved changes. Are you sure you want to discard them?
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1 }}>
            <Button
              onClick={() => setDiscardDialogOpen(false)}
              sx={{ textTransform: "none", color: "#111111" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDiscardConfirmed}
              sx={{ textTransform: "none" }}
            >
              Discard
            </Button>
          </Stack>
        }
      />

      {/* Navigation blocker dialog */}
      <CustomConfirmDialog
        open={showBlockerDialog}
        title="Unsaved Changes"
        onClose={() => {
          setShowBlockerDialog(false);
          if (blockerRef.current?.state === "blocked") {
            blockerRef.current.reset();
          }
        }}
        content={
          <Typography>
            You have unsaved changes. Are you sure you want to leave this page?
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1 }}>
            <Button
              onClick={() => {
                setShowBlockerDialog(false);
                if (blockerRef.current?.state === "blocked") {
                  blockerRef.current.reset();
                }
              }}
              sx={{ textTransform: "none", color: "#111111" }}
            >
              Stay
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setShowBlockerDialog(false);
                if (blockerRef.current?.state === "blocked") {
                  blockerRef.current.proceed();
                }
              }}
              sx={{ textTransform: "none" }}
            >
              Leave
            </Button>
          </Stack>
        }
      />
    </>
  );
}
